/**
 * "Current UPB · Purchase basis" — the lead card for a purchased loan.
 * Used as the first KPI on the Summary tab and at the top of the UPB section
 * on Trade economics. Values come from upbFacts (upbModel.js); nothing here is
 * invented — a row whose field is empty shows "—" or is left out.
 *
 *   CURRENT UPB · PURCHASE BASIS                    (i)
 *   $2,600,000.00
 *   As of cut-off Oct 15, 2026 · [Matches Original UPB]
 *   ─────────────────────────────────────────────
 *   ORIGINAL UPB   PAID DOWN | DRAWN   ACCRUED INT.
 *   VIEW UPB BREAKDOWN ↓   → Original → ± change → Current → accrued → price
 */
import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { FONT_MONO } from "../../tabs/LoanDrilldownSummaryTab/summaryShared";
import { slate, status, steel, teal } from "../../../../theme/colors";
import { money, shortDate, upbFacts } from "./upbModel";

const INK = slate[900];
const INK3 = slate[500];

const Eyebrow = ({ children, color = INK3 }) => (
  <Txt
    component="div"
    sx={{
      fontSize: 10.5,
      letterSpacing: "0.09em",
      textTransform: "uppercase",
      fontWeight: 650,
      color,
    }}
  >
    {children}
  </Txt>
);

Eyebrow.propTypes = { children: PropTypes.node, color: PropTypes.string };

const Tag = ({ tone, children }) => (
  <Box
    component="span"
    sx={{
      ml: "6px",
      px: "6px",
      py: "1px",
      borderRadius: "5px",
      fontSize: 10.5,
      fontWeight: 600,
      background: tone === "teal" ? teal[100] : steel.tint,
      color: tone === "teal" ? teal[700] : steel.main,
    }}
  >
    {children}
  </Box>
);

Tag.propTypes = { tone: PropTypes.string, children: PropTypes.node };

const BreakdownRow = ({ label, value, strong }) => (
  <Flx
    jb
    ac
    sx={{
      gap: "12px",
      py: "9px",
      borderBottom: `1px dashed ${slate[200]}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Txt
      sx={{
        fontSize: 13,
        color: strong ? INK : slate[600],
        fontWeight: strong ? 650 : 400,
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 13,
        fontWeight: strong ? 700 : 500,
        color: value == null ? INK3 : INK,
        whiteSpace: "nowrap",
      }}
    >
      {value ?? "—"}
    </Txt>
  </Flx>
);

BreakdownRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  strong: PropTypes.bool,
};

const UpbLeadCard = ({ loan, sx }) => {
  const f = upbFacts(loan);
  const [open, setOpen] = useState(false);

  const grew = f.change != null && f.change > 0;
  const chip =
    f.change == null
      ? null
      : f.change === 0
        ? { text: "Matches Original UPB", ...status.success }
        : grew
          ? {
              text: `${money(f.change)} drawn since origination`,
              ...status.info,
            }
          : { text: `${money(-f.change)} paid down`, ...status.neutral };

  const accruedLabel =
    f.accruedDays != null && f.paidTo
      ? `Accrued interest · ${f.accruedDays}d from paid-to ${f.paidTo.toLocaleDateString(
          "en-US",
          { month: "short", day: "numeric" },
        )}`
      : "Accrued interest";

  return (
    <Box
      sx={{
        background: `linear-gradient(155deg, ${teal[50]}, #ffffff 70%)`,
        border: `1px solid ${teal[200]}`,
        borderRadius: "14px",
        p: "18px 20px 16px",
        ...sx,
      }}
    >
      <Flx ac jb sx={{ gap: "8px" }}>
        <Eyebrow color={teal[700]}>Current UPB · Purchase basis</Eyebrow>
        <Tooltip
          arrow
          placement="top"
          title="The unpaid principal balance being purchased. The purchase price and the trade's interest strip are calculated from it."
        >
          <InfoOutlined sx={{ fontSize: 15, color: INK3, cursor: "help" }} />
        </Tooltip>
      </Flx>

      <Txt
        component="div"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 30,
          fontWeight: 650,
          letterSpacing: "-0.02em",
          color: f.currentUPB == null ? INK3 : INK,
          mt: "8px",
        }}
      >
        {f.currentUPB == null ? "—" : money(f.currentUPB, 2)}
      </Txt>

      <Flx ac sx={{ gap: "6px", mt: "4px", flexWrap: "wrap" }}>
        <Txt sx={{ fontSize: 12.5, color: slate[600] }}>
          {f.cutOffIsFirm ? "As of cut-off" : "As of settlement"}{" "}
          <Box component="span" sx={{ fontWeight: 650, color: INK }}>
            {shortDate(f.cutOff) || "—"}
          </Box>
        </Txt>
        {chip && (
          <Flx
            ac
            sx={{
              gap: "3px",
              px: "8px",
              py: "2px",
              borderRadius: "6px",
              background: chip.bg,
            }}
          >
            {f.change !== 0 &&
              (grew ? (
                <ArrowUpwardRounded sx={{ fontSize: 13, color: chip.fg }} />
              ) : (
                <ArrowDownwardRounded sx={{ fontSize: 13, color: chip.fg }} />
              ))}
            <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: chip.fg }}>
              {chip.text}
            </Txt>
          </Flx>
        )}
      </Flx>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "12px",
          mt: "14px",
          pt: "14px",
          borderTop: `1px solid ${teal[150]}`,
        }}
      >
        {[
          ["Original UPB", money(f.originalUPB)],
          [
            grew ? "Drawn" : "Paid down",
            f.change == null ? null : money(Math.abs(f.change)),
          ],
          ["Accrued int.", money(f.accruedInterest, 2)],
        ].map(([label, value]) => (
          <Flx column key={label} sx={{ gap: "3px", minWidth: 0 }}>
            <Eyebrow>{label}</Eyebrow>
            <Txt
              sx={{
                fontFamily: FONT_MONO,
                fontSize: 14,
                fontWeight: 650,
                color: value == null || value === "$0" ? INK3 : INK,
              }}
            >
              {value ?? "—"}
            </Txt>
          </Flx>
        ))}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{
          all: "unset",
          cursor: "pointer",
          mt: "14px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: 11.5,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          fontWeight: 650,
          color: teal[700],
          "&:hover": { color: teal[800] },
          "&:focus-visible": {
            outline: `2px solid ${teal[300]}`,
            outlineOffset: 2,
          },
        }}
      >
        {open ? "Hide breakdown" : "View UPB breakdown"}
        {open ? (
          <ArrowUpwardRounded sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardRounded sx={{ fontSize: 14 }} />
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: "12px",
            px: "16px",
            py: "6px",
            background: "#ffffff",
            border: `1px solid ${slate[200]}`,
            borderRadius: "10px",
          }}
        >
          <BreakdownRow
            label={
              <>
                Original UPB<Tag>whole note</Tag>
              </>
            }
            value={money(f.originalUPB, 2)}
          />
          {f.change != null && (
            <BreakdownRow
              label={
                grew ? "Plus draws since origination" : "Less principal paid"
              }
              value={money(Math.abs(f.change), 2)}
            />
          )}
          <BreakdownRow
            strong
            label={
              <>
                Current UPB<Tag tone="teal">pricing</Tag>
              </>
            }
            value={money(f.currentUPB, 2)}
          />
          <BreakdownRow
            label={accruedLabel}
            value={money(f.accruedInterest, 2)}
          />
          {(f.bid != null || f.purchasePriceIsStored) && (
            <BreakdownRow
              label={
                f.bid != null
                  ? `Purchase price @ ${f.bid.toFixed(3)}`
                  : "Purchase price"
              }
              value={money(f.purchasePrice, 2)}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

UpbLeadCard.propTypes = {
  loan: PropTypes.object,
  sx: PropTypes.object,
};

export default UpbLeadCard;
